import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, MapIcon, SearchIcon, LightbulbIcon, CheckIcon } from 'lucide-react';
import { TestimonialCard } from '../components/TestimonialCard';
import { PurchaseSection } from '../components/PurchaseSection';
import { PurchasePopup } from '../components/PurchasePopup';
export function LandingPage() {
  const scrollToPurchase = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-neutral-900 text-neutral-100 font-sans selection:bg-amber-500 selection:text-black" data-id="element-47">
      <PurchasePopup data-id="element-48" />

      {/* Hero Section */}
      <header className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-id="element-49">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-50">
          <img src="https://cdn.magicpatterns.com/uploads/cq8FVohtcnZtzchbmAqmex/ChatGPT_Image_4_ene_2026,_18_29_04.png" alt="Mercado argentino" className="w-full h-full object-cover cursor-pointer" onClick={scrollToPurchase} data-id="element-51" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-neutral-900" data-id="element-52"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl pt-20" data-id="element-53">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-54">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white drop-shadow-lg" data-id="element-55">
              Descubre qué consumen los argentinos todos los días…{' '}
              <span className="text-amber-500 block mt-2" data-id="element-56">
                y gana dinero con eso
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-neutral-200 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-57">
              ¿Sabías que mientras muchos argentinos luchan por llegar a fin de
              mes, otros convierten los simples hábitos de consumo en ingresos
              constantes y crecientes?
            </p>

            <motion.button onClick={scrollToPurchase} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-xl py-4 px-10 rounded-full shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all duration-300" data-id="element-58">
              QUIERO SABER CÓMO
            </motion.button>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 1
      }} className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-neutral-400" data-id="element-59">
          <ArrowDownIcon className="w-8 h-8" data-id="element-60" />
        </motion.div>
      </header>

      {/* Pain Points Section */}
      <section className="py-20 bg-neutral-900" data-id="element-61">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-62">
          <div className="text-center mb-16" data-id="element-63">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white" data-id="element-64">
              ¿Te suena familiar esto?
            </h2>
            <div className="h-1 w-20 bg-amber-500 mx-auto rounded-full" data-id="element-65"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-66">
            {['Intentas un negocio que parecía prometedor y, al poco tiempo, el dinero desaparece.', 'Sigues cursos de “hacerse rico rápido” que funcionan en otros países, pero fracasan aquí.', 'Trabajas mucho, pero tus ingresos no crecen.', 'La inflación, el dólar y los cambios políticos hacen que cualquier plan financiero se sienta frágil e incierto.'].map((point, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-neutral-800 p-6 rounded-xl border-l-4 border-red-500 shadow-md" data-id="element-67">
                <p className="text-lg text-neutral-200" data-id="element-68">{point}</p>
              </motion.div>)}
          </div>

          <div className="mt-12 text-center bg-neutral-800/50 p-8 rounded-2xl border border-neutral-700" data-id="element-69">
            <p className="text-xl text-neutral-300 italic" data-id="element-70">
              "Esa sensación de que mientras otros prosperan, tú simplemente
              sobrevives… es frustrante, y no es culpa tuya. El problema es que
              nadie te enseñó cómo aprovechar lo que los argentinos realmente
              compran cada día."
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-neutral-800 relative overflow-hidden" data-id="element-71">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-black/20 to-transparent pointer-events-none" data-id="element-72"></div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-73">
          <div className="text-center mb-16" data-id="element-74">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-amber-500" data-id="element-75">
              Imagina un escenario distinto
            </h2>
            <p className="text-xl text-neutral-300" data-id="element-76">
              Con{' '}
              <span className="font-bold text-white" data-id="element-77">
                Qué consumen los argentinos y cómo ganar dinero con eso
              </span>
              , esto no es un sueño: es una acción concreta y posible.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-78">
            {[{
            icon: SearchIcon,
            text: 'Conocer exactamente qué compran los argentinos todos los días.'
          }, {
            icon: LightbulbIcon,
            text: 'Identificar oportunidades de negocio que otros ni siquiera ven.'
          }, {
            icon: MapIcon,
            text: 'Transformar esa información en ingresos concretos y sostenibles.'
          }, {
            icon: CheckIcon,
            text: 'Tener claridad, seguridad y control sobre tu economía, incluso en un país inestable.'
          }].map((item, i) => <motion.div key={i} whileHover={{
            y: -5
          }} className="bg-neutral-700/50 p-6 rounded-xl text-center border border-neutral-600 hover:border-amber-500 transition-colors" data-id="element-79">
                <div className="bg-amber-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-80">
                  <item.icon className="w-8 h-8 text-amber-500" data-id="element-81" />
                </div>
                <p className="text-neutral-200 font-medium" data-id="element-82">{item.text}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why It Works (Metaphors) */}
      <section className="py-20 bg-neutral-900" data-id="element-83">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-84">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-85">
            Por qué esta guía funciona donde otros fallan
          </h2>

          <div className="space-y-8" data-id="element-86">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="flex flex-col md:flex-row items-center gap-6 bg-neutral-800 p-8 rounded-2xl border border-neutral-700" data-id="element-87">
              <div className="bg-amber-500/10 p-4 rounded-full flex-shrink-0" data-id="element-88">
                <MapIcon className="w-10 h-10 text-amber-500" data-id="element-89" />
              </div>
              <div data-id="element-90">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-91">
                  Un mapa del tesoro
                </h3>
                <p className="text-neutral-300" data-id="element-92">
                  Cada hábito de consumo es una “X” que marca dónde está el
                  dinero. No más adivinanzas, solo datos reales.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="flex flex-col md:flex-row items-center gap-6 bg-neutral-800 p-8 rounded-2xl border border-neutral-700" data-id="element-93">
              <div className="bg-amber-500/10 p-4 rounded-full flex-shrink-0" data-id="element-94">
                <SearchIcon className="w-10 h-10 text-amber-500" data-id="element-95" />
              </div>
              <div data-id="element-96">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-97">
                  Una lupa sobre el mercado
                </h3>
                <p className="text-neutral-300" data-id="element-98">
                  Revela detalles invisibles que otros pasan por alto. Encuentra
                  nichos desatendidos listos para ser explotados.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="flex flex-col md:flex-row items-center gap-6 bg-neutral-800 p-8 rounded-2xl border border-neutral-700" data-id="element-99">
              <div className="bg-amber-500/10 p-4 rounded-full flex-shrink-0" data-id="element-100">
                <LightbulbIcon className="w-10 h-10 text-amber-500" data-id="element-101" />
              </div>
              <div data-id="element-102">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-103">
                  Una linterna en la oscuridad
                </h3>
                <p className="text-neutral-300" data-id="element-104">
                  Ilumina oportunidades reales en un mercado confuso. Deja de
                  tropezar y empieza a caminar con seguridad.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-neutral-800" data-id="element-105">
        <div className="container mx-auto px-4 max-w-3xl text-center" data-id="element-106">
          <h2 className="text-3xl font-bold mb-8 text-white" data-id="element-107">
            La historia detrás de esta guía
          </h2>
          <div className="prose prose-invert prose-lg mx-auto text-neutral-300" data-id="element-108">
            <p className="mb-6" data-id="element-109">
              El creador de este documento estuvo exactamente donde tú estás
              ahora: frustrado, perdido y cansado de métodos que no funcionaban
              en Argentina.
            </p>
            <p className="mb-6" data-id="element-110">
              Después de años investigando foros, redes y tiendas locales,
              descubrió patrones de consumo ocultos y rentables. Con esa
              información, comenzó a generar ingresos sostenibles mientras otros
              seguían perdiendo dinero.
            </p>
            <p className="font-bold text-white text-xl" data-id="element-111">
              Hoy comparte ese conocimiento contigo para que no tengas que
              perder tiempo ni dinero descubriendo lo que ya está frente a tus
              ojos.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-neutral-900" data-id="element-112">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-113">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-white" data-id="element-114">
            Argentinos que ya están ganando
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-115">
            <TestimonialCard name="Martín" age={34} city="Buenos Aires" quote="Estaba frustrado probando negocios que no funcionaban. Esta guía me mostró oportunidades reales que tenía frente a mis ojos. Ahora genero ingresos constantes." delay={0} data-id="element-116" />
            <TestimonialCard name="Carolina" age={28} city="Córdoba" quote="Como freelancer, mis ingresos eran impredecibles. Después de aplicar estas estrategias, duplicé mis ingresos en 3 meses." delay={0.1} data-id="element-117" />
            <TestimonialCard name="Roberto" age={45} city="Rosario" quote="Tengo un comercio hace años pero nunca crecía. Esta información me ayudó a identificar nuevas oportunidades y aumentar mis ventas un 40%." delay={0.2} data-id="element-118" />
            <TestimonialCard name="Lucía" age={31} city="Mendoza" quote="Trabajo full-time pero quería ingresos extra. Ahora tengo un ingreso pasivo mensual gracias a lo que aprendí aquí." delay={0.3} data-id="element-119" />
            <TestimonialCard name="Diego" age={38} city="La Plata" quote="Como padre de familia, necesitaba estabilidad económica. Esta guía me dio claridad y seguridad financiera que nunca tuve." delay={0.4} data-id="element-120" />
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-neutral-800" data-id="element-121">
        <div className="container mx-auto px-4 max-w-4xl text-center" data-id="element-122">
          <h2 className="text-3xl md:text-4xl font-bold mb-12" data-id="element-123">
            Qué recibirás hoy
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-124">
            <div className="bg-neutral-700/30 p-6 rounded-xl border border-neutral-600" data-id="element-125">
              <div className="text-4xl mb-4" data-id="element-126">📊</div>
              <h3 className="font-bold text-lg mb-2" data-id="element-127">Datos Reales</h3>
              <p className="text-neutral-400" data-id="element-128">
                Qué compran los argentinos realmente, sin filtros ni teorías.
              </p>
            </div>
            <div className="bg-neutral-700/30 p-6 rounded-xl border border-neutral-600" data-id="element-129">
              <div className="text-4xl mb-4" data-id="element-130">💰</div>
              <h3 className="font-bold text-lg mb-2" data-id="element-131">Monetización</h3>
              <p className="text-neutral-400" data-id="element-132">
                Cómo convertir hábitos en oportunidades de negocio o ventas.
              </p>
            </div>
            <div className="bg-neutral-700/30 p-6 rounded-xl border border-neutral-600" data-id="element-133">
              <div className="text-4xl mb-4" data-id="element-134">🚀</div>
              <h3 className="font-bold text-lg mb-2" data-id="element-135">Estrategias</h3>
              <p className="text-neutral-400" data-id="element-136">
                Pasos inmediatos para generar ingresos reales ya mismo.
              </p>
            </div>
          </div>

          <p className="text-xl text-neutral-300 mb-8" data-id="element-137">
            No esperes más. Cada día que pasa, estás dejando dinero en la mesa.
          </p>
        </div>
      </section>

      {/* Purchase Section */}
      <PurchaseSection data-id="element-138" />

      {/* Footer */}
      <footer className="bg-black py-8 text-center text-neutral-600 text-sm" data-id="element-139">
        <div className="container mx-auto px-4" data-id="element-140">
          <p className="mb-2" data-id="element-141">
            © {new Date().getFullYear()} Qué consumen los argentinos. Todos los
            derechos reservados.
          </p>
          <p data-id="element-142">Economía real, hábitos diarios y oportunidades concretas.</p>
        </div>
      </footer>
    </div>;
}